import React, { useMemo, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { api, ApiError, type Profile } from '../lib/api';
import { AppIcon } from './Logo';
import {
  BirthDateFields,
  CountryField,
  ConsentFields,
  ageVerdict,
  consentReady,
  isBirthDateReady,
  Field,
  INPUT,
} from './ProfileFields';

interface Props {
  profile: Profile;
  onCompleted: (profile: Profile) => void;
  /** La cuenta se borró (no tenía edad para existir) o se cerró la sesión. */
  onSignedOut: (message?: string) => void;
}

/**
 * Pide los datos que a una cuenta le faltan para poder usarse.
 *
 * Hace falta porque Google no entrega ni fecha de nacimiento ni país: quien
 * entra por ahí llega con una cuenta válida a la que le faltan dos campos
 * obligatorios. Lo mismo vale para las cuentas creadas antes de que lo fueran.
 *
 * No se puede cerrar y no ofrece salida: la sesión ya está abierta, así que
 * dejar seguir sin los datos sería tener el requisito solo de adorno. Cerrar
 * sesión sí está disponible, que es la única alternativa honesta a rellenarlo.
 */
export const CompleteProfileModal: React.FC<Props> = ({ profile, onCompleted, onSignedOut }) => {
  const displayName = profile.displayName;
  const [birth, setBirth] = useState({ day: '', month: '', year: '' });
  const [country, setCountry] = useState(profile.country ?? '');
  // Google los entrega partidos cuando los tiene; si no, se piden en blanco.
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');
  // El usuario es tambien el nombre publico, y a quien llega por Google se le
  // ha derivado del correo. Se puede cambiar aqui mismo, antes de que aparezca
  // proyectado en la pantalla de una partida.
  const [usuario, setUsuario] = useState(profile.username);
  const [consent, setConsent] = useState({ acceptTerms: false, guardianConsent: false });
  const verdict = useMemo(() => ageVerdict(birth, country), [birth, country]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Sin edad para tener cuenta, lo único que queda es no guardar sus datos:
  // el botón cambia a "borrar" y el servidor elimina la cuenta de Google.
  const tooYoung = verdict !== null && !verdict.ok;

  const canSubmit = useMemo(
    () =>
      !busy &&
      isBirthDateReady(birth) &&
      Boolean(country) &&
      (tooYoung ||
        (usuario.trim().length >= 3 &&
          firstName.trim().length >= 2 &&
          lastName.trim().length >= 2 &&
          consentReady(verdict, consent))),
    [busy, birth, country, usuario, firstName, lastName, verdict, consent, tooYoung],
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await api.post<{ profile: Profile }>('/auth/complete-profile', {
        birthDate: `${birth.year}-${birth.month}-${birth.day}`,
        country,
        username: usuario.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        acceptTerms: consent.acceptTerms,
        guardianConsent: verdict?.ok && verdict.needsGuardian ? consent.guardianConsent : false,
      });
      onCompleted(data.profile);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar.';
      // Si la cuenta ya no existe es que el servidor la borró por la edad.
      try {
        await api.get('/auth/me');
      } catch (me) {
        if (me instanceof ApiError && me.status === 401) {
          onSignedOut(`${message} Hemos borrado los datos que nos llegaron de Google.`);
          return;
        }
      }
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="complete-title"
    >
      <div className="surface-raised bg-ink-900 w-full max-w-[26rem] my-auto animate-pop">
        <header className="flex items-start gap-3 px-6 pt-6 pb-5">
          <AppIcon size={32} />
          <div className="flex-1 min-w-0">
            <h2
              id="complete-title"
              className="font-display font-semibold text-[1.0625rem] text-ink-50"
            >
              Casi listo, {displayName}
            </h2>
            <p className="text-ink-400 text-[0.8125rem] leading-snug mt-0.5">
              Un último paso para terminar tu cuenta.
            </p>
          </div>
        </header>

        <form onSubmit={submit} className="px-6 pb-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre">
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={40}
                required
                autoComplete="given-name"
                className={INPUT}
              />
            </Field>
            <Field label="Apellido">
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={40}
                required
                autoComplete="family-name"
                className={INPUT}
              />
            </Field>
          </div>

          <Field
            label="Usuario"
            hint="3 a 20 caracteres: letras, números, punto o guion bajo. Es el nombre con el que te verán los demás."
          >
            <div className="relative">
              <input
                value={usuario}
                // Se filtra al escribir con la MISMA regla que aplica el servidor.
                onChange={(e) =>
                  setUsuario(
                    e.target.value
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .replace(/[^A-Za-z0-9_.]/g, ''),
                  )
                }
                maxLength={20}
                required
                autoFocus
                className={INPUT}
              />
            </div>
          </Field>

          <BirthDateFields value={birth} onChange={setBirth} />
          <CountryField value={country} onChange={setCountry} />

          <p className="text-[0.6875rem] leading-relaxed text-ink-500">
            Google no nos facilita la edad ni el país, y la edad mínima para tener
            cuenta depende de la ley de cada país. Tu nombre real y tu fecha de
            nacimiento no se muestran a nadie: en las partidas solo aparece tu usuario.
          </p>

          <ConsentFields verdict={verdict} value={consent} onChange={setConsent} />

          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-danger-400/30 bg-danger-400/10 px-3 py-2.5 text-danger-400 text-[0.8125rem]"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-lg font-semibold
                       bg-ember-500 hover:bg-ember-400 text-on-accent
                       disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {tooYoung ? 'Borrar mis datos y salir' : 'Continuar'}
          </button>

          <button
            type="button"
            onClick={() => onSignedOut()}
            className="w-full text-center text-[0.8125rem] text-ink-400 hover:text-ink-100"
          >
            Cerrar sesión
          </button>
        </form>
      </div>
    </div>
  );
};
